import { Link, Navigate, useNavigate } from "react-router-dom";
import { T } from "@/components/T";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { fetchGamify, getProfile, saveReminders, fetchMyNotes, getEbookActivity, premiumBirthStatus, premiumDeleteBirth, requestPasswordReset } from "@/lib/api";
import { CalendarCheck, Bookmark, LogOut, LayoutDashboard, Trophy, Flame, UserCircle, Bell, NotebookPen, ListChecks, BookOpen, ShieldCheck, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { imgSrc } from "@/lib/images";
import EmailPreferences from "@/components/EmailPreferences";

export default function Account() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const { pick } = useLang();
  const [gamify, setGamify] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ebookAct, setEbookAct] = useState([]);
  const [openBook, setOpenBook] = useState(null);

  useEffect(() => { if (ready && user) { fetchGamify().then(setGamify).catch(() => {}); getProfile().then(setProfile).catch(() => {}); getEbookActivity().then((d) => setEbookAct(d.items || [])).catch(() => {}); } }, [ready, user]);

  const toggleReminder = async (key) => {
    const r = { ...(profile?.reminders || {}), [key]: !(profile?.reminders?.[key] ?? true) };
    setProfile((p) => ({ ...p, reminders: r }));
    try { await saveReminders({ [key]: r[key] }); } catch { /* ignore */ }
  };

  if (ready && !user) return <Navigate to="/login" replace />;
  if (!user) return <div className="py-32 text-center text-ink-3">…</div>;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-20">
      <div className="flex items-center gap-4">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--surface-alt)] font-serif-latin text-2xl text-saffron">
            {(user.name || user.email || "?")[0].toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="font-serif-latin text-3xl text-ink">{user.name || "Namaste"}</h1>
          <p className="text-sm text-ink-3">{user.email}</p>
        </div>
      </div>

      {gamify && (
        <Link to="/leaderboard" data-testid="account-gamify-card" className="mt-10 flex flex-wrap items-center justify-between gap-4 border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)] p-6 transition-colors hover:border-[var(--gold)]">
          <div className="flex items-center gap-4">
            <Trophy className="h-7 w-7 text-gold" />
            <div>
              <p className="font-deva text-xl text-ink deva-lh">{gamify.level.deva}</p>
              <p className="text-sm text-ink-2">{gamify.level.en} · {gamify.badges.length} {gamify.badges.length === 1 ? "badge" : "badges"}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right"><p className="font-serif-latin text-2xl text-saffron">{gamify.points}</p><p className="text-xs uppercase tracking-widest text-ink-3"><T>points</T></p></div>
            {gamify.streak > 0 && <div className="text-right"><p className="inline-flex items-center gap-1 font-serif-latin text-2xl text-saffron"><Flame className="h-4 w-4" />{gamify.streak}</p><p className="text-xs uppercase tracking-widest text-ink-3"><T>streak</T></p></div>}
          </div>
        </Link>
      )}

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Link to="/onboarding" data-testid="account-profile-card" className="flex items-center gap-4 border border-line bg-surface p-6 transition-colors hover:border-[var(--gold-muted)]">
          <UserCircle className="h-6 w-6 text-saffron" />
          <div className="min-w-0 flex-1">
            <p className="font-serif-latin text-lg text-ink"><T>My Profile</T></p>
            <p className="text-sm text-ink-3">{profile ? (profile.completion >= 100 ? "Complete — thank you!" : `${profile.completion || 0}% complete · finish & earn points`) : "Interests, deities & goals"}</p>
            {profile != null && profile.completion < 100 && <div className="mt-2 h-1 w-full overflow-hidden bg-line"><div className="h-full bg-[var(--gold)]" style={{ width: `${profile.completion || 0}%` }} /></div>}
          </div>
        </Link>
        <Link to="/my-bookings" className="flex items-center gap-4 border border-line bg-surface p-6 transition-colors hover:border-[var(--gold-muted)]">
          <CalendarCheck className="h-6 w-6 text-saffron" />
          <div><p className="font-serif-latin text-lg text-ink"><T>My Bookings</T></p><p className="text-sm text-ink-3"><T>Pooja, yagya & consultation requests</T></p></div>
        </Link>
        <Link to="/bookmarks" className="flex items-center gap-4 border border-line bg-surface p-6 transition-colors hover:border-[var(--gold-muted)]">
          <Bookmark className="h-6 w-6 text-saffron" />
          <div><p className="font-serif-latin text-lg text-ink"><T>Bookmarks</T></p><p className="text-sm text-ink-3"><T>Saved knowledge entries</T></p></div>
        </Link>
        <Link to="/notes" data-testid="account-notes-card" className="flex items-center gap-4 border border-line bg-surface p-6 transition-colors hover:border-[var(--gold-muted)]">
          <NotebookPen className="h-6 w-6 text-saffron" />
          <div><p className="font-serif-latin text-lg text-ink"><T>My Notes</T></p><p className="text-sm text-ink-3"><T>Your private notes across pages</T></p></div>
        </Link>
        <Link to="/checklists" data-testid="account-checklists-card" className="flex items-center gap-4 border border-line bg-surface p-6 transition-colors hover:border-[var(--gold-muted)]">
          <ListChecks className="h-6 w-6 text-saffron" />
          <div><p className="font-serif-latin text-lg text-ink"><T>My Pooja Checklists</T></p><p className="text-sm text-ink-3"><T>Preparation lists you can tick off</T></p></div>
        </Link>
        <Link to="/leaderboard" className="flex items-center gap-4 border border-line bg-surface p-6 transition-colors hover:border-[var(--gold-muted)]">
          <Trophy className="h-6 w-6 text-saffron" />
          <div><p className="font-serif-latin text-lg text-ink"><T>Sadhana & Badges</T></p><p className="text-sm text-ink-3"><T>Your points, level and leaderboard</T></p></div>
        </Link>
        {user.role === "admin" && (
          <Link to="/admin" className="flex items-center gap-4 border border-line bg-surface p-6 transition-colors hover:border-[var(--gold-muted)]">
            <LayoutDashboard className="h-6 w-6 text-saffron" />
            <div><p className="font-serif-latin text-lg text-ink"><T>Admin Console</T></p><p className="text-sm text-ink-3"><T>Manage content & bookings</T></p></div>
          </Link>
        )}
      </div>

      {/* Practice reminders */}
      <div className="mt-10 border border-line bg-surface p-6" data-testid="account-reminders">
        <p className="inline-flex items-center gap-2 font-serif-latin text-lg text-ink"><Bell className="h-5 w-5 text-saffron" /> <T>Practice reminders</T></p>
        <p className="mt-1 text-sm text-ink-3"><T>Gentle in-app nudges via your notification bell.</T></p>
        <div className="mt-4 flex flex-wrap gap-2">
          {[["festivals", "Festivals & Pooja"], ["japa", "Japa"], ["daily_pooja", "Daily Pooja"], ["sandhya", "Sandhya Vandana"]].map(([k, l]) => {
            const on = profile?.reminders?.[k] ?? true;
            return (
              <button key={k} onClick={() => toggleReminder(k)} data-testid={`reminder-toggle-${k}`}
                className={`border px-3 py-1.5 text-sm transition-colors ${on ? "border-saffron bg-saffron text-white" : "border-line text-ink-2 hover:border-saffron"}`}>
                {l}
              </button>
            );
          })}
        </div>
      </div>

      <EmailPreferences />

      <SecurityCard email={user.email} provider={user.provider} />

      {/* eBook reading progress */}
      {ebookAct.length > 0 && (
        <div className="mt-10 border border-line bg-surface p-6" data-testid="account-ebook-progress">
          <p className="inline-flex items-center gap-2 font-serif-latin text-lg text-ink"><BookOpen className="h-5 w-5 text-saffron" /> {pick("पठन प्रगति · ग्रंथ", "Reading progress · eBooks")}</p>
          <p className="mt-1 text-sm text-ink-3">{pick("आपके बुकमार्क, टिप्पणियाँ एवं अंतिम पढ़ा पृष्ठ।", "Your bookmarks, notes and last-read page.")}</p>
          <div className="mt-4 space-y-3">
            {ebookAct.map((bk) => {
              const t = pick(bk.title_hi, bk.title) || bk.title;
              const isOpen = openBook === bk.slug;
              return (
                <div key={bk.slug} className="border border-line/70" data-testid={`ebook-progress-${bk.slug}`}>
                  <div className="flex items-center gap-3 p-3">
                    <div className="h-16 w-12 shrink-0 overflow-hidden rounded border border-line bg-parchment">
                      {bk.cover_url ? <img src={imgSrc(bk.cover_url, 120)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-gold/50"><BookOpen className="h-5 w-5" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/books/${bk.slug}${bk.page > 1 ? `?page=${bk.page}` : ""}`} className="line-clamp-1 font-serif-latin text-base text-ink hover:text-saffron">{t}</Link>
                      <p className="mt-0.5 text-xs text-ink-3">
                        {bk.page > 1 ? pick(`पृष्ठ ${bk.page} तक पढ़ा`, `Read to page ${bk.page}`) : pick("आरंभ नहीं", "Not started")}
                        {" · "}{bk.bookmarks.length} {pick("बुकमार्क", "bookmarks")} · {bk.notes.length} {pick("टिप्पणी", "notes")}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {bk.page > 1 && <Link to={`/books/${bk.slug}?page=${bk.page}`} data-testid={`ebook-resume-${bk.slug}`} className="rounded-full bg-[var(--saffron)] px-3 py-1.5 text-xs font-medium text-white">{pick("जारी रखें", "Resume")}</Link>}
                      {(bk.bookmarks.length > 0 || bk.notes.length > 0) && (
                        <button onClick={() => setOpenBook(isOpen ? null : bk.slug)} data-testid={`ebook-progress-toggle-${bk.slug}`} className="rounded-full border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-saffron hover:text-saffron">{isOpen ? pick("छिपाएँ", "Hide") : pick("देखें", "View")}</button>
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="grid gap-4 border-t border-line/70 p-3 sm:grid-cols-2">
                      <div>
                        <p className="mb-1.5 flex items-center gap-1.5 text-xs uppercase tracking-wider text-ink-3"><Bookmark className="h-3.5 w-3.5" /> {pick("बुकमार्क", "Bookmarks")}</p>
                        {bk.bookmarks.length === 0 ? <p className="text-xs text-ink-3">—</p> : (
                          <ul className="flex flex-wrap gap-1.5">
                            {bk.bookmarks.map((m) => <li key={m.id}><Link to={`/books/${bk.slug}?page=${m.page}`} className="inline-flex items-center gap-1 rounded-full border border-line px-2 py-1 text-xs text-ink-2 hover:border-saffron hover:text-saffron"><span className="tabular-nums"><T>p.</T>{m.page}</span> <span className="line-clamp-1 max-w-[120px]">{m.label}</span></Link></li>)}
                          </ul>
                        )}
                      </div>
                      <div>
                        <p className="mb-1.5 flex items-center gap-1.5 text-xs uppercase tracking-wider text-ink-3"><NotebookPen className="h-3.5 w-3.5" /> {pick("टिप्पणियाँ", "Notes")}</p>
                        {bk.notes.length === 0 ? <p className="text-xs text-ink-3">—</p> : (
                          <ul className="space-y-1.5">
                            {bk.notes.map((n) => (
                              <li key={n.id} className="text-xs text-ink-2">
                                <Link to={`/books/${bk.slug}?page=${n.page}`} className="mr-1.5 rounded-full bg-parchment px-1.5 py-0.5 tabular-nums text-ink-3 hover:text-saffron"><T>p.</T>{n.page}</Link>
                                <span className="line-clamp-2 align-middle">{n.text}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <BirthDataPrivacy />

      <button data-testid="account-logout" onClick={doLogout} className="mt-10 inline-flex items-center gap-2 border border-line px-6 py-3 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
        <LogOut className="h-4 w-4" /> <T>Sign out</T>
      </button>
    </div>
  );
}

function SecurityCard({ email, provider }) {
  const { pick } = useLang();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (provider && provider !== "password") return null; // Google-only accounts have no password
  const reset = async () => {
    setBusy(true);
    try { await requestPasswordReset(email); setSent(true); toast.success(pick("रीसेट लिंक आपके ईमेल पर भेज दिया गया।", "A reset link has been sent to your email.")); }
    catch { toast.error(pick("भेजने में असमर्थ", "Could not send the reset link")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-10 border border-line bg-surface p-6" data-testid="account-security">
      <p className="inline-flex items-center gap-2 font-serif-latin text-lg text-ink"><KeyRound className="h-5 w-5 text-saffron" /> {pick("सुरक्षा", "Security")}</p>
      <p className="mt-1 text-sm text-ink-3">{pick("अपना पासवर्ड बदलने हेतु अपने ईमेल पर एक सुरक्षित रीसेट लिंक प्राप्त करें।", "Get a secure reset link emailed to you to change your password.")}</p>
      <button onClick={reset} disabled={busy || sent} data-testid="account-reset-password"
        className="mt-3 inline-flex items-center gap-1.5 border border-saffron/50 px-4 py-2 text-sm text-saffron transition-colors hover:bg-saffron hover:text-white disabled:opacity-60">
        <KeyRound className="h-4 w-4" /> {sent ? pick("लिंक भेजा गया ✓", "Link sent ✓") : busy ? pick("भेजा जा रहा…", "Sending…") : pick("पासवर्ड रीसेट करें", "Reset password")}
      </button>
    </div>
  );
}

function BirthDataPrivacy() {
  const { pick } = useLang();
  const [status, setStatus] = useState(null);
  useEffect(() => { premiumBirthStatus().then(setStatus).catch(() => {}); }, []);
  if (!status?.exists) return null;
  const remove = async () => {
    if (!window.confirm(pick("अपना जन्म-विवरण स्थायी रूप से हटाएँ?", "Permanently delete your stored birth details?"))) return;
    try { await premiumDeleteBirth(); setStatus({ exists: false }); toast.success(pick("आपका जन्म-विवरण हटा दिया गया", "Your birth details were deleted")); }
    catch { toast.error(pick("हटाने में असमर्थ", "Could not delete")); }
  };
  return (
    <div className="mt-10 border border-[var(--gold-muted)]/50 bg-[rgba(176,141,87,0.05)] p-5" data-testid="account-birth-privacy">
      <h3 className="flex items-center gap-2 font-serif-latin text-lg text-ink"><ShieldCheck className="h-5 w-5 text-gold" /> {pick("आपका डेटा एवं गोपनीयता", "Your data & privacy")}</h3>
      <p className="mt-1.5 text-sm text-ink-2">{pick(
        "आपका जन्म-विवरण संग्रहीत है (DPDP अधिनियम के अंतर्गत संवेदनशील डेटा)। आप इसे कभी भी स्थायी रूप से हटा सकते हैं — चाहे प्रीमियम सक्रिय हो या नहीं।",
        "Your birth details are stored (sensitive data under the DPDP Act). You can permanently delete them at any time — whether or not Premium is active.")}
        {status.consent_at && <span className="mt-1 block text-xs text-ink-3">{pick("सहमति", "Consent")}: {String(status.consent_at).slice(0, 10)} · {status.place}</span>}
      </p>
      <button onClick={remove} data-testid="account-delete-birth" className="mt-3 inline-flex items-center gap-1.5 border border-saffron/50 px-4 py-2 text-sm text-saffron transition-colors hover:bg-saffron hover:text-white">
        <Trash2 className="h-4 w-4" /> {pick("मेरा जन्म-विवरण हटाएँ", "Delete my birth details")}
      </button>
    </div>
  );
}
